import { useTranslation } from "react-i18next";
import clsx from "clsx";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { Checkbox, Select, SelectOption, Switcher } from "@/shared/ui";
import SettingsControlLabel from "@/domains/chat/components/settings/SettingsLabel";
import SettingsTooltip from "@/domains/chat/components/settings/SettingsTooltip";
import useChatValues from "@/domains/settings/chat/hooks/useChatValues";
import { appliedChatSettingsSelector, setAppliedSettings } from "@/domains/settings/chat/slice";

export default function SettingsBodyMessageContainer() {
  const { t } = useTranslation("chat");
  const dispatch = useAppDispatch();
  const {
    messageHeader,
    highlightOwnMessages,
    showBadge,
    showTime,
    showSpeakMessages,
    showRPMessages,
    showNewsMessages,
    showGovernmentNews,
    showFactionMessages,
    showGovernmentChat,
    showFamilyMessages,
    showServerMessages,
    showRadioMessages,
    showSms,
    typeRankPlayerFormat,
  } = useAppSelector(appliedChatSettingsSelector);
  const { typeRankPlayerFormatValues } = useChatValues();

  const commonChatFilterItemsLeft = [
    {
      label: t("chat.settings.speakMessages"),
      checked: showSpeakMessages,
      onChange: () =>
        dispatch(
          setAppliedSettings({
            showSpeakMessages: !showSpeakMessages,
          }),
        ),
    },
    {
      label: t("chat.settings.rp"),
      checked: showRPMessages,
      onChange: () =>
        dispatch(
          setAppliedSettings({
            showRPMessages: !showRPMessages,
          }),
        ),
    },
    {
      label: t("chat.settings.news"),
      checked: showNewsMessages,
      onChange: () =>
        dispatch(
          setAppliedSettings({
            showNewsMessages: !showNewsMessages,
          }),
        ),
    },
    {
      label: t("chat.settings.govNews"),
      checked: showGovernmentNews,
      onChange: () =>
        dispatch(
          setAppliedSettings({
            showGovernmentNews: !showGovernmentNews,
          }),
        ),
    },
    {
      label: t("chat.settings.factions"),
      checked: showFactionMessages,
      onChange: () =>
        dispatch(
          setAppliedSettings({
            showFactionMessages: !showFactionMessages,
          }),
        ),
    },
  ];

  const commonChatFilterItemsRight = [
    {
      label: t("chat.settings.govChat"),
      checked: showGovernmentChat,
      onChange: () =>
        dispatch(
          setAppliedSettings({
            showGovernmentChat: !showGovernmentChat,
          }),
        ),
    },
    {
      label: t("chat.settings.family"),
      checked: showFamilyMessages,
      onChange: () =>
        dispatch(
          setAppliedSettings({
            showFamilyMessages: !showFamilyMessages,
          }),
        ),
    },
    {
      label: t("chat.settings.serverMessages"),
      checked: showServerMessages,
      onChange: () =>
        dispatch(
          setAppliedSettings({
            showServerMessages: !showServerMessages,
          }),
        ),
    },
    {
      label: t("chat.settings.radio"),
      checked: showRadioMessages,
      onChange: () =>
        dispatch(
          setAppliedSettings({
            showRadioMessages: !showRadioMessages,
          }),
        ),
    },
    {
      label: t("chat.settings.sms"),
      checked: showSms,
      onChange: () =>
        dispatch(
          setAppliedSettings({
            showSms: !showSms,
          }),
        ),
    },
  ];

  const switcherList = [
    {
      label: t("chat.settings.messageHeader"),
      checked: messageHeader,
      onChange: () =>
        dispatch(
          setAppliedSettings({
            messageHeader: !messageHeader,
          }),
        ),
      tooltip: t("chat.settings.messageHeaderTooltip"),
    },
    {
      label: t("chat.settings.highlightOwnMessages"),
      checked: highlightOwnMessages,
      onChange: () =>
        dispatch(
          setAppliedSettings({
            highlightOwnMessages: !highlightOwnMessages,
          }),
        ),
      tooltip: t("chat.settings.highlightOwnMessagesTooltip"),
    },
    {
      label: t("chat.settings.showBadge"),
      checked: showBadge,
      onChange: () =>
        dispatch(
          setAppliedSettings({
            showBadge: !showBadge,
          }),
        ),
      tooltip: t("chat.settings.showBadgeTooltip"),
    },
    {
      label: t("chat.settings.showTime"),
      checked: showTime,
      onChange: () =>
        dispatch(
          setAppliedSettings({
            showTime: !showTime,
          }),
        ),
      tooltip: t("chat.settings.showTimeTooltip"),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div>
        <div className="flex items-center justify-between">
          <SettingsControlLabel>{t("chat.settings.filtersCommonChat")}</SettingsControlLabel>
          <SettingsTooltip text={t("chat.settings.filtersCommonChatTooltip")} />
        </div>
        <div className="grid grid-cols-2 items-start gap-1 mt-3">
          <div className="flex flex-col gap-2">
            {commonChatFilterItemsLeft.map((item) => (
              <div key={item.label} className="flex items-start gap-2">
                <Checkbox checked={item.checked} onChange={item.onChange} />
                <div className="text-13 font-bold">{item.label}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-col  gap-2">
            {commonChatFilterItemsRight.map((item) => (
              <div key={item.label} className="flex items-start gap-2">
                <Checkbox checked={item.checked} onChange={item.onChange} />
                <div className="text-13 font-bold">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div>
        <SettingsControlLabel>{t("chat.settings.rankVisibility")} </SettingsControlLabel>
        <Select value={typeRankPlayerFormat} className="mt-3 z-[2]" bg="bg-dark-800">
          <div className="flex flex-col">
            {typeRankPlayerFormatValues.map((item) => (
              <SelectOption
                key={item.value}
                onClick={() => dispatch(setAppliedSettings({ typeRankPlayerFormat: item.value }))}
                isSelected={item.value === typeRankPlayerFormat}
                className="hover:text-accent-solid transition-all"
              >
                {t(item.label)}
              </SelectOption>
            ))}
          </div>
        </Select>
      </div>
      <div className="w-full h-fit flex flex-col rounded-[0.625rem] border border-dark-800">
        {switcherList.map((item, index) => (
          <div
            key={item.label}
            className={clsx(
              "h-[3.125rem] px-3.5 flex items-center justify-between border-dark-800",
              { "border-t": index > 0 },
            )}
          >
            <div className="text-14 font-bold">{item.label}</div>
            <SettingsTooltip text={item.tooltip} className="ml-auto" />
            <Switcher on={item.checked} onChange={item.onChange} withBorder />
          </div>
        ))}
      </div>
    </div>
  );
}
